// UtilitiesManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';

const UtilitiesManagement = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [editedAiStatus, setEditedAiStatus] = useState('');
  const [editedGlobalStatus, setEditedGlobalStatus] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/maintenance`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tools');
      }

      const data = await response.json();
      
      // Transform the data to match the expected format
      const transformedTools = data.map(tool => ({
        tool_id: tool.tool_id,
        tool_name: tool.tool_name,
        tool_category: tool.tool_category,
        global_status: tool.status,
        ai_integration_status: tool.ai_integration?.[0]?.status || 'operational',
      }));

      setTools(transformedTools);
      setError(null);
    } catch (err) {
      console.error('Error fetching tools:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (tool) => {
    setSelectedTool(tool);
    setEditedAiStatus(tool.ai_integration_status);
    setEditedGlobalStatus(tool.global_status);
    setEditDialogOpen(true);
  };

  // Handle save updates - sends tool_id to API
  const handleSaveUpdate = async () => {
    if (!selectedTool) return;

    setUpdateLoading(true);
    try {
      
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/maintenance`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            tool_id: selectedTool.tool_id, // Hidden tool_id sent to API
            ai_integration_status: editedAiStatus,
            status: editedGlobalStatus,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update tool');
      }

      // Show success message
      setSnackbar({
        open: true,
        message: 'Tool status updated successfully!',
        severity: 'success',
      });

      // Refresh tools list after successful update
      await fetchTools();
      setEditDialogOpen(false);
      setSelectedTool(null);
    } catch (err) {
      console.error('Error updating tool:', err);
      setSnackbar({
        open: true,
        message: `Error updating tool: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setUpdateLoading(false);
    }
  };

  // Get status chip color
  const getStatusColor = (status) => {
    return status?.toLowerCase() === 'operational' ? 'success' : 'warning';
  };

  // Get status chip label
  const getStatusLabel = (status) => {
    return status?.toLowerCase() === 'operational' ? 'Operational' : 'Maintenance';
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Error loading tools: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 400 }}>
        Utilities/Tools
      </Typography>

      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 500, color: '#333', border: 'none' }}>
                Tool Name
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#333', border: 'none' }}>
                AI Integration Status
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#333', border: 'none' }}>
                Global Status
              </TableCell>
              <TableCell sx={{ fontWeight: 500, color: '#333', border: 'none' }}>
                Edit
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tools.map((tool) => (
              <TableRow 
                key={tool.tool_id}
                sx={{
                  '&:hover': { backgroundColor: '#fafafa' },
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <TableCell sx={{ border: 'none', py: 2 }}>
                  {tool.tool_name}
                </TableCell>
                <TableCell sx={{ border: 'none', py: 2 }}>
                  <Chip
                    label={getStatusLabel(tool.ai_integration_status)}
                    color={getStatusColor(tool.ai_integration_status)}
                    size="small"
                    sx={{ 
                      fontWeight: 500,
                      minWidth: '110px',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ border: 'none', py: 2 }}>
                  <Chip
                    label={getStatusLabel(tool.global_status)}
                    color={getStatusColor(tool.global_status)}
                    size="small"
                    sx={{ 
                      fontWeight: 500,
                      minWidth: '110px',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ border: 'none', py: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleEditClick(tool)}
                    sx={{
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    EDIT
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {tools.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No tools available</Typography>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => !updateLoading && setEditDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Edit Tool Status</DialogTitle>
        <DialogContent>
          {selectedTool && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Tool:</strong> {selectedTool.tool_name}
              </Typography>
              
              {/* Hidden tool_id - displayed for demo purposes */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Tool ID: {selectedTool.tool_id}
              </Typography>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>AI Integration Status</InputLabel>
                <Select
                  value={editedAiStatus}
                  onChange={(e) => setEditedAiStatus(e.target.value)}
                  label="AI Integration Status"
                >
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Global Status</InputLabel>
                <Select
                  value={editedGlobalStatus}
                  onChange={(e) => setEditedGlobalStatus(e.target.value)}
                  label="Global Status"
                >
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDialogOpen(false)} 
            disabled={updateLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUpdate}
            variant="contained"
            disabled={updateLoading}
          >
            {updateLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UtilitiesManagement;